// Patient age helpers.
//
// Problem this solves
// ───────────────────
// The codebase previously computed age as `floor(years between DOB and today)`
// and used the integer result for both display and the child-vs-adult split
// (boundary at 12 years). That collapsed two distinct correctness issues:
//
//   1) Infants under one year were shown as **0**, hiding clinically
//      important detail. "0 years" is ambiguous between a newborn and an
//      eleven-month-old who should be classified differently for dosing.
//
//   2) Anything that compared `age >= 12` against a rounded integer is one
//      truncation bug away from misclassifying a ten-year-eleven-month child
//      as an adult — which then routes them through adult medication tables.
//
// Storage format
// ──────────────
// The spec asks for a decimal encoding so a single numeric column can carry
// both years and months without ambiguity:
//
//     decimal = years + months / 100   (months always 00–11)
//
// Examples:
//     11 years  9 months  → 11.09
//     10 years 11 months  → 10.11
//      0 years 11 months  →  0.11
//      0 years  2 months  →  0.02
//      0 years  0 months  →  0.00   (days carried in the label only)
//
// Days never appear in the decimal because months naturally cap at 11 — a
// 12-month decimal would collide with the next year. The day-resolution
// component is preserved as a separate field on the returned object and is
// only used to render labels like "2m 28d" for infants where it matters.
//
// Child vs adult
// ──────────────
// `isAdult` is the strict integer-years check `years >= 12`. Because we keep
// the *floor* of completed years (never rounding up), an 11y11m patient
// returns `years = 11` and stays a child until their actual birthday.

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function toDate(value) {
  if (!value) return null;
  // Accept Date, dayjs, moment, ISO strings, and YYYY-MM-DD.
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value : null;
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && Number.isFinite(d.getTime()) ? d : null;
  }
  if (typeof value === 'object' && typeof value.format === 'function') {
    // moment / dayjs — fall through to a date string we can parse.
    const iso = value.format('YYYY-MM-DD');
    const d = new Date(iso);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
}

// Calendar-aware years/months/days between two dates. Returns the floor of
// each whole component — never rounds up, so a patient born 11y11m29d ago
// returns { years: 11, months: 11, days: 29 } rather than rolling over.
function calendarDiff(from, to) {
  let years  = to.getFullYear() - from.getFullYear();
  let months = to.getMonth()    - from.getMonth();
  let days   = to.getDate()     - from.getDate();

  if (days < 0) {
    months -= 1;
    // Number of days in the month *prior* to `to` — that's where the borrow
    // comes from. JS Date with day=0 returns the last day of the previous month.
    const prevMonthEnd = new Date(to.getFullYear(), to.getMonth(), 0).getDate();
    days += prevMonthEnd;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return { years, months, days };
}

/**
 * Compute a patient's age from a date of birth.
 *
 * @param {Date|string|object} dob - DOB as Date, ISO string, dayjs or moment.
 * @param {Date}              [now] - Optional reference "today" (testing).
 * @returns {{
 *   years: number,
 *   months: number,
 *   days: number,
 *   totalDays: number,
 *   decimal: number,        // YY.MM with months 00–11
 *   label: string,          // "10y 11 mon" / "11 mon 28d" / "28d"
 *   shortLabel: string,     // "10y" / "11 mon" / "28d" — for tight UI
 *   isAdult: boolean,       // years >= 12 (never rounded up)
 *   group: 'newborn'|'infant'|'child'|'adult'
 * } | null}
 */
export function computeAge(dob, now = new Date()) {
  const birth = toDate(dob);
  if (!birth) return null;
  if (birth.getTime() > now.getTime()) {
    // Future DOBs are nonsensical — treat as unknown rather than negative age.
    return null;
  }

  const { years, months, days } = calendarDiff(birth, now);
  const totalDays = Math.floor((now.getTime() - birth.getTime()) / MS_PER_DAY);

  // months / 100 is exact in IEEE-754 only for a handful of values, so we
  // round to two decimals to avoid 0.30000000000000004 surprises in displays.
  const decimal = Number.parseFloat((years + months / 100).toFixed(2));

  // Label policy: only include components that are non-zero and informative.
  // For under-1-month patients we still show 0d so the field isn't blank.
  let label;
  //  The month is always "mon" — the facility's standard abbreviation.
  if (years > 0 && months > 0) label = `${years}y ${months} mon`;
  else if (years > 0)          label = `${years}y`;
  else if (months > 0 && days > 0) label = `${months} mon ${days}d`;
  else if (months > 0)         label = `${months} mon`;
  else                         label = `${days}d`;

  const shortLabel =
    years  > 0 ? `${years}y`  :
    months > 0 ? `${months} mon` :
                 `${days}d`;

  const isAdult = years >= 12;
  const group =
    totalDays <= 28 ? 'newborn' :
    years === 0     ? 'infant'  :
    isAdult         ? 'adult'   : 'child';

  return { years, months, days, totalDays, decimal, label, shortLabel, isAdult, group };
}

/**
 * Convenience wrapper for the adult classification (12 years 0 days and over).
 * Falls back to a numeric age (years only) when DOB is missing so existing
 * call sites that only have a cached integer keep working.
 */
export function isAdultFromDobOrAge(dob, fallbackAge) {
  const ag = computeAge(dob);
  if (ag) return ag.isAdult;
  const n = Number(fallbackAge);
  return Number.isFinite(n) && n >= 12;
}

/**
 * "child" / "adult" string used by the dosing tables. Same precedence as
 * `isAdultFromDobOrAge` — prefer DOB, fall back to a cached numeric age.
 */
export function ageGroupLabel(dob, fallbackAge) {
  return isAdultFromDobOrAge(dob, fallbackAge) ? 'adult' : 'child';
}

/**
 * Format an age for display. Pass DOB when available — falls back to the
 * cached integer years (rendered as "Ny") so legacy lists that only carry
 * `patient.age` still show *something* sensible.
 */
export function ageDisplay(dob, fallbackAge) {
  const ag = computeAge(dob);
  if (ag) return ag.label;
  if (fallbackAge === 0 || fallbackAge === '0') return '<1y';
  if (fallbackAge === null || fallbackAge === undefined || fallbackAge === '') return '';
  return `${fallbackAge}y`;
}
